'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useFrame } from '@react-three/fiber';
import { useXR, useXRInputSourceState } from '@react-three/xr';
import { Text } from '@react-three/drei';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { applyDeadZone, isPressed } from '../_pure/spaceCanvasHelpers.js';
import { combineXrStick, easeXrKeyboardSticks, stepXrKeyboardSticks, XR_KEYBOARD_IDLE } from '../_pure/xrKeyboard.js';
import { resolveXrLocomotion, turnXrRig } from '../_pure/xrLocomotion.js';
import { collectXrTravelGeometry, createXrTravelState, resolveXrTravel } from '../_pure/xrTravel.js';
import { XR_TRAVEL_GEOMETRY_REFRESH_SECONDS } from '../_data/spaceCanvas.js';
import { reportXrDiagnostic, useVisualizationDiagnostics } from '../../runtime/diagnostics.js';
import { spaceCanvasXrBindings } from '../xrBindings.js';
import { spaceCanvasXrKeyboard } from '../xrKeyboard.js';
// Diagnostics must not distort what they measure: the HUD text re-lays out rarely and samples upload in batches.
const XR_DIAGNOSTIC_LABEL_SECONDS = 1;
const XR_DIAGNOSTIC_SAMPLE_SECONDS = 5;
export default function SpaceCanvasXrLocomotion({ cameraRigRef, canNavigate, travelSceneRef, }) {
    const { xrLocomotion } = useVisualizationDiagnostics();
    const session = useXR((state) => state.session);
    const leftController = useXRInputSourceState('controller', 'left');
    const rightController = useXRInputSourceState('controller', 'right');
    const cameraDirection = useMemo(() => new THREE.Vector3(), []);
    const forward = useMemo(() => new THREE.Vector3(), []);
    const right = useMemo(() => new THREE.Vector3(), []);
    const move = useMemo(() => new THREE.Vector3(), []);
    const up = useMemo(() => new THREE.Vector3(0, 1, 0), []);
    const eye = useMemo(() => new THREE.Vector3(), []);
    const turn = useMemo(() => new THREE.Quaternion(), []);
    const indicator = useRef(null);
    const labelRef = useRef('');
    const [label, setLabel] = useState('');
    const travelState = useRef(createXrTravelState());
    const leftAxis = useRef(undefined);
    const travelGeometry = useRef(undefined);
    const geometryAge = useRef(Infinity);
    const labelAge = useRef(Infinity);
    const labelMultiplier = useRef(1);
    const keyRamp = useRef(XR_KEYBOARD_IDLE);
    const frameWindow = useRef({ age: 0, worst: 0, shown: 0 });
    // Per-frame rows for the batched XR diagnostic sample: [frame ms, forward key ramp %, right-stick Y %,
    // rig step mm, navigation enabled 0/1].
    const sampleRows = useRef([]);
    const sampleAge = useRef(0);
    const sampleSequence = useRef(0);
    const sampleFrame = (delta, step, stickY, enabled) => {
        if (!xrLocomotion || !session)
            return;
        sampleRows.current.push([Math.round(delta * 1000), Math.round(-keyRamp.current.rightY * 100),
            Math.round(-stickY * 100), Math.round(step * 1000), enabled ? 1 : 0]);
        sampleAge.current += Math.max(0, delta);
        if (sampleAge.current < XR_DIAGNOSTIC_SAMPLE_SECONDS)
            return;
        const stats = spaceCanvasXrKeyboard.stats();
        reportXrDiagnostic({
            sequence: ++sampleSequence.current, at: Date.now(), now: Math.round(performance.now()),
            blend: session.environmentBlendMode ?? 'unknown', framerate: session.frameRate ?? null,
            cruise: spaceCanvasXrKeyboard.cruising(), keys: spaceCanvasXrKeyboard.drainTrace(),
            counts: { ...stats, shortestRegrabMs: Number.isFinite(stats.shortestRegrabMs) ? stats.shortestRegrabMs : null },
            focus: typeof document === 'undefined' ? null : document.hasFocus(), frames: sampleRows.current,
        });
        sampleRows.current = [];
        sampleAge.current = 0;
    };
    const keyboardDiagnostic = () => {
        const stats = spaceCanvasXrKeyboard.stats();
        const ms = (value) => Number.isFinite(value) ? `${Math.round(value)}ms` : '-';
        const age = Number.isFinite(stats.lastEventMs) ? performance.now() - stats.lastEventMs : NaN;
        return `Keys cruising: ${spaceCanvasXrKeyboard.cruising().join(',') || '-'} | ramp fwd ${(-keyRamp.current.rightY).toFixed(2)}`
            + ` | worst frame ${ms(frameWindow.current.shown * 1000)}\n`
            + `Key events: down ${stats.downs} repeat ${stats.repeats} up ${stats.ups} focus-stops ${stats.releases}`
            + ` | shortest re-press ${ms(stats.shortestRegrabMs)} | longest held silence ${ms(stats.longestHeldSilenceMs)}`
            + ` | last ${ms(age)} ago`;
    };
    useEffect(() => {
        keyRamp.current = XR_KEYBOARD_IDLE;
        travelState.current = createXrTravelState();
        leftAxis.current = undefined;
        travelGeometry.current = undefined;
        geometryAge.current = Infinity;
    }, [session]);
    /** Diagnostic labels are passed lazily so their text is only built when it may be shown. */
    const updateLabel = (next, multiplier = 1) => {
        const lazy = typeof next === 'function';
        const due = labelAge.current >= (lazy ? XR_DIAGNOSTIC_LABEL_SECONDS : 0.1) || labelRef.current === ''
            || multiplier !== labelMultiplier.current;
        const nextLabel = lazy ? due ? next() : labelRef.current : next;
        if (lazy && due)
            labelAge.current = 0;
        if (labelRef.current !== nextLabel && (due || nextLabel === '')) {
            labelRef.current = nextLabel;
            labelAge.current = 0;
            labelMultiplier.current = multiplier;
            setLabel(nextLabel);
        }
        if (indicator.current)
            indicator.current.visible = Boolean(nextLabel);
    };
    useFrame((state, delta) => {
        labelAge.current += Math.max(0, delta);
        frameWindow.current.age += Math.max(0, delta);
        frameWindow.current.worst = Math.max(frameWindow.current.worst, delta);
        if (frameWindow.current.age >= 1)
            frameWindow.current = { age: 0, worst: delta, shown: frameWindow.current.worst };
        const rig = cameraRigRef.current;
        const enabled = Boolean(session && rig && canNavigate());
        const leftSqueeze = leftController?.gamepad['xr-standard-squeeze'];
        const leftGrip = enabled ? leftSqueeze?.button ?? (isPressed(leftSqueeze) ? 1 : 0) : 0;
        const leftThumbstick = leftController?.gamepad['xr-standard-thumbstick'];
        const leftTrigger = leftController?.gamepad['xr-standard-trigger'];
        const rightThumbstick = rightController?.gamepad['xr-standard-thumbstick'];
        const rightCaptured = spaceCanvasXrBindings.capturesStick('right');
        const leftTriggerCaptured = spaceCanvasXrBindings.capturesTrigger('left');
        keyRamp.current = enabled ? stepXrKeyboardSticks(keyRamp.current, spaceCanvasXrKeyboard.sticks(), delta) : XR_KEYBOARD_IDLE;
        const keys = easeXrKeyboardSticks(keyRamp.current);
        const motion = resolveXrLocomotion({
            leftX: combineXrStick(leftThumbstick?.xAxis ?? 0, keys.leftX),
            leftY: combineXrStick(leftThumbstick?.yAxis ?? 0, keys.leftY),
            leftTrigger: leftTriggerCaptured ? 0 : leftTrigger?.button ?? (isPressed(leftTrigger) ? 1 : 0),
            rightX: combineXrStick(rightCaptured ? 0 : rightThumbstick?.xAxis ?? 0, keys.rightX),
            rightY: combineXrStick(rightCaptured ? 0 : rightThumbstick?.yAxis ?? 0, keys.rightY),
            leftGrip: enabled ? Math.max(leftGrip, keys.boost) : 0, ...(leftAxis.current ? { leftAxis: leftAxis.current } : {}),
        }, delta);
        leftAxis.current = enabled ? motion.leftAxis : undefined;
        const diagnosticLabel = xrLocomotion ? () => {
            const axes = leftController?.inputSource.gamepad?.axes;
            const pressed = (controller) => {
                const buttons = controller?.inputSource.gamepad?.buttons;
                if (!buttons)
                    return 'none';
                const indices = Array.from(buttons, (button, index) => button.pressed ? index : -1).filter(index => index >= 0);
                return indices.length ? indices.join(',') : '-';
            };
            return `XR input: ${enabled ? 'enabled' : 'blocked'} | left: ${leftController ? 'connected' : 'missing'}\n`
                + `Mapped X: ${(leftThumbstick?.xAxis ?? 0).toFixed(2)} | Mapped Y: ${(leftThumbstick?.yAxis ?? 0).toFixed(2)}`
                + ` | left axis: ${motion.leftAxis ?? 'none'}\n`
                + `Raw axes: ${axes ? Array.from(axes, value => value.toFixed(2)).join(', ') : 'none'}\n`
                + `Pressed buttons: left ${pressed(leftController)} | right ${pressed(rightController)}\n`
                + `Yaw step: ${motion.yaw.toFixed(4)} | rig Y: ${(rig?.quaternion.y ?? 0).toFixed(4)}\n`
                + keyboardDiagnostic();
        } : undefined;
        if (!enabled || !rig) {
            travelState.current = createXrTravelState();
            geometryAge.current = Infinity;
            updateLabel(diagnosticLabel && session ? diagnosticLabel : '');
            sampleFrame(delta, 0, rightThumbstick?.yAxis ?? 0, false);
            return;
        }
        state.camera.getWorldPosition(eye);
        if (motion.yaw !== 0)
            turnXrRig(rig, eye, motion.yaw, turn);
        if (indicator.current) {
            state.camera.getWorldQuaternion(indicator.current.quaternion);
            indicator.current.position.set(0, -0.5, -1.5)
                .applyQuaternion(indicator.current.quaternion).add(eye);
        }
        state.camera.getWorldDirection(cameraDirection);
        forward.set(cameraDirection.x, 0, cameraDirection.z);
        if (forward.lengthSq() < 1e-8) {
            forward.set(0, 0, -1).applyQuaternion(rig.quaternion);
            forward.y = 0;
            forward.normalize();
        }
        else {
            forward.normalize();
        }
        right.crossVectors(forward, up).normalize();
        move.set(0, 0, 0);
        move.addScaledVector(right, motion.x);
        move.addScaledVector(forward, motion.forward);
        move.y += motion.y;
        if (move.lengthSq() > 0) {
            geometryAge.current += Math.max(0, delta);
            if (!travelGeometry.current || geometryAge.current >= XR_TRAVEL_GEOMETRY_REFRESH_SECONDS) {
                if (!travelSceneRef.current)
                    throw new Error('XR navigation scene is not mounted.');
                travelGeometry.current = collectXrTravelGeometry(travelSceneRef.current);
                geometryAge.current = 0;
            }
            const travel = resolveXrTravel({
                eye, direction: move, geometry: travelGeometry.current, previous: travelState.current,
                multiplier: motion.multiplier, throttle: motion.throttle, delta,
            });
            travelState.current = travel.state;
            move.normalize().multiplyScalar(travel.distance);
        }
        else {
            travelState.current = createXrTravelState();
            geometryAge.current = Infinity;
        }
        const speed = travelState.current.speed;
        updateLabel(diagnosticLabel ?? (motion.throttle > 0 || applyDeadZone(leftGrip) > 0
            ? `Travel speed: ${motion.multiplier.toFixed(2)}x | ${speed.toPrecision(3)} units/s` : ''), motion.multiplier);
        rig.position.add(move);
        rig.updateMatrixWorld(true);
        indicator.current?.position.add(move);
        sampleFrame(delta, move.length(), rightCaptured ? 0 : rightThumbstick?.yAxis ?? 0, true);
    });
    return _jsx("group", { ref: indicator, visible: false, children: _jsxs(Text, { fontSize: 0.045, textAlign: "center", anchorX: "center", anchorY: "middle", raycast: () => null, renderOrder: 10013, children: [label, _jsx("meshBasicMaterial", { depthTest: false, depthWrite: false })] }) });
}
